import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { isNativePlatform } from "@/lib/platform";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Bookmark, ArrowLeft, Sparkles, Home, LogIn } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { getCategoryForStyle } from "@/data/designStyles";
import { ShareDesignPopover } from "@/components/ShareDesignPopover";
import { attachPublicUsernames } from "@/lib/public-designers";

const VariationViewer = ({ mainImage, allVariations, designStyle, roomType }: {
  mainImage: string;
  allVariations?: string[];
  designStyle: string;
  roomType: string;
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const images = allVariations && allVariations.length > 1 ? allVariations : [mainImage];
  const currentImage = images[activeIdx] || mainImage;

  return (
    <div>
      <img
        src={currentImage}
        alt={`${designStyle} ${roomType}`}
        className="w-full h-auto max-h-[50vh] md:max-h-[70vh] object-contain bg-black/5"
      />
      {images.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto">
          {images.map((url, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`shrink-0 rounded-md overflow-hidden border-2 transition-colors ${
                i === activeIdx ? "border-primary" : "border-transparent hover:border-border"
              }`}
            >
              <img src={url} alt={`Variation ${i + 1}`} className="w-16 h-16 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface DesignData {
  id: string;
  imageUrl: string;
  designStyle: string;
  roomType: string;
  username: string;
  customPrompt?: string | null;
  designerNote?: string | null;
  createdAt: string;
  allVariations?: string[];
}

interface SimilarDesign {
  id: string;
  transformed_image_url: string | null;
  original_image_url: string;
  design_style: string;
  room_type: string;
  designer_note: string | null;
  public_variations: string[] | null;
  profiles: { username: string } | null;
}

const getSimilarCover = (sd: SimilarDesign): string => {
  if (Array.isArray(sd.public_variations) && sd.public_variations.length > 0) {
    const first = sd.public_variations[0];
    if (typeof first === "string" && first) return first;
  }
  return sd.transformed_image_url || sd.original_image_url;
};

interface ExploreDesignModalProps {
  open: boolean;
  onClose: () => void;
  design: DesignData | null;
  likeCount: number;
  isLiked: boolean;
  isSaved: boolean;
  onLike: (designId: string) => void;
  onSave: (designId: string) => void;
  onDesignClick: (design: SimilarDesign) => void;
  likedIds: Set<string>;
  savedIds: Set<string>;
  likeCounts: Record<string, number>;
}

export type { SimilarDesign };

export const ExploreDesignModal = ({
  open,
  onClose,
  design,
  likeCount,
  isLiked,
  isSaved,
  onLike,
  onSave,
  onDesignClick,
  likedIds,
  savedIds,
  likeCounts,
}: ExploreDesignModalProps) => {
  const navigate = useNavigate();
  const [similarDesigns, setSimilarDesigns] = useState<SimilarDesign[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const fetchSimilar = useCallback(async (d: DesignData) => {
    setLoadingSimilar(true);
    const { data } = await supabase
      .from("saved_designs")
      .select("id, user_id, transformed_image_url, original_image_url, design_style, room_type, designer_note, public_variations")
      .eq("is_public", true)
      .neq("id", d.id)
      .or(`design_style.eq.${d.designStyle},room_type.eq.${d.roomType}`)
      .order("created_at", { ascending: false })
      .limit(9);

    const withUsernames = await attachPublicUsernames((data || []) as any[]);
    setSimilarDesigns(withUsernames as unknown as SimilarDesign[]);
    setLoadingSimilar(false);
  }, []);

  useEffect(() => {
    if (open && design) {
      fetchSimilar(design);
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
        leftPaneRef.current?.scrollTo({ top: 0, behavior: "auto" });
        rightPaneRef.current?.scrollTo({ top: 0, behavior: "auto" });
      });
    } else {
      setSimilarDesigns([]);
    }
  }, [open, design?.id]);

  // Intercept browser/device back button so it closes the modal instead of leaving /explore.
  // IMPORTANT: depend ONLY on `open`. `onClose` is a fresh function on every parent render
  // (likes/saves/similar-click cause re-renders), so depending on it would tear down the
  // effect mid-session and the cleanup's `history.back()` would close the modal unexpectedly.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const consumedSentinelRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    consumedSentinelRef.current = false;
    // Push a sentinel history entry we can pop on close
    window.history.pushState({ __exploreModal: true }, "");

    const onPop = () => {
      consumedSentinelRef.current = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);

    let removeNative: (() => void) | undefined;
    if (isNativePlatform()) {
      const handlePromise = CapacitorApp.addListener("backButton", () => {
        // Triggers popstate above via history.back()
        window.history.back();
      });
      removeNative = () => {
        handlePromise.then((h) => h.remove()).catch(() => {});
      };
    }

    return () => {
      window.removeEventListener("popstate", onPop);
      removeNative?.();
      // If modal was closed via in-app UI (not via popstate), pop the sentinel ourselves
      if (!consumedSentinelRef.current && window.history.state?.__exploreModal) {
        window.history.back();
      }
    };
  }, [open]);

  if (!design) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        hideCloseButton
        className="w-screen h-screen max-w-none max-h-none rounded-none p-0 gap-0 border-0 flex flex-col"
      >
        <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10 safe-area-inset-top shrink-0">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Back"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold capitalize">
                    {design.designStyle}
                  </h1>
                  <p className="text-sm text-muted-foreground capitalize">
                    {design.roomType}
                  </p>
                </div>
              </div>
              {!isNativePlatform() && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => { onClose(); navigate("/"); }}
                  >
                    <Home className="h-4 w-4" />
                    <span className="hidden sm:inline">Home</span>
                  </Button>
                  {!user && (
                    <Button
                      variant="default"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => { onClose(); navigate("/auth"); }}
                    >
                      <LogIn className="h-4 w-4" />
                      <span className="hidden sm:inline">Sign In</span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        <div ref={scrollRef} className="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
          {/* Left: Selected design */}
          <div ref={leftPaneRef} className="md:w-1/2 flex-shrink-0 md:overflow-y-auto md:h-full">
            <VariationViewer
              mainImage={design.imageUrl}
              allVariations={design.allVariations}
              designStyle={design.designStyle}
              roomType={design.roomType}
            />
            <div className="p-4 md:p-6 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  {getCategoryForStyle(design.designStyle) && (
                    <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-medium">
                      {getCategoryForStyle(design.designStyle)}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">{design.designStyle}</Badge>
                    <Badge variant="outline">{design.roomType}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => onLike(design.id)}>
                    <Heart className={`h-4 w-4 ${isLiked ? "fill-red-500 text-red-500" : ""}`} />
                    {likeCount > 0 && <span className="text-sm">{likeCount}</span>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onSave(design.id)}>
                    <Bookmark className={`h-4 w-4 ${isSaved ? "fill-primary text-primary" : ""}`} />
                  </Button>
                  <ShareDesignPopover
                    designId={design.id}
                    designStyle={design.designStyle}
                    roomType={design.roomType}
                    imageUrl={design.imageUrl}
                    variations={design.allVariations}
                  />
                </div>
              </div>

              <Button
                variant="default"
                size="sm"
                className="w-full gap-2"
                onClick={() => {
                  onClose();
                  const params = new URLSearchParams({
                    style: design.designStyle,
                    room: design.roomType,
                  });
                  if (design.customPrompt) params.set("prompt", design.customPrompt);
                  navigate(`/designer?${params.toString()}`);
                }}
              >
                <Sparkles className="h-4 w-4" />
                Try This Style
              </Button>

              {design.designerNote && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Designer's Thought</p>
                  <p className="text-sm italic">"{design.designerNote}"</p>
                </div>
              )}

              {design.customPrompt && (
                <div className="bg-accent/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Custom Idea</p>
                  <p className="text-sm">"{design.customPrompt}"</p>
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>by @{design.username}</span>
                {(() => {
                  const created = design.createdAt ? new Date(design.createdAt) : null;
                  return created && !isNaN(created.getTime()) ? (
                    <span>{created.toLocaleDateString()}</span>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Right: Similar designs */}
          <div ref={rightPaneRef} className="md:w-1/2 border-t md:border-t-0 md:border-l border-border p-4 md:p-6 md:overflow-y-auto md:h-full">
            <h3 className="text-base font-semibold mb-3">More like this</h3>
            {loadingSimilar ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="w-full aspect-[3/4] rounded-lg" />
                ))}
              </div>
            ) : similarDesigns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No similar designs found yet.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {similarDesigns.map((sd) => {
                  const imgUrl = getSimilarCover(sd);
                  const sdLiked = likedIds.has(sd.id);
                  const sdSaved = savedIds.has(sd.id);
                  const sdLikeCount = likeCounts[sd.id] || 0;
                  return (
                    <div
                      key={sd.id}
                      className="group cursor-pointer rounded-lg overflow-hidden border border-border/50 hover:border-primary/30 transition-all"
                      onClick={() => onDesignClick(sd)}
                    >
                      <div className="relative">
                        <img
                          src={imgUrl}
                          alt={`${sd.design_style} ${sd.room_type}`}
                          className="w-full aspect-[3/4] object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-1.5 space-y-1">
                        <p className="text-[10px] text-muted-foreground truncate">
                          @{sd.profiles?.username || "anonymous"}
                        </p>
                        <div className="flex items-center gap-1">
                          <Heart className={`h-3 w-3 ${sdLiked ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
                          {sdLikeCount > 0 && <span className="text-[10px] text-muted-foreground">{sdLikeCount}</span>}
                          <Bookmark className={`h-3 w-3 ml-auto ${sdSaved ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
